/**
 * Preparazione delle foto prima dell'upload.
 *
 * PERCHÉ ESISTE
 * nginx sul VPS rifiuta le richieste sopra 1 MB e l'upload viaggia in base64,
 * che gonfia del 33%: una foto da telefono (3-5 MB) non passerebbe mai. Qui la
 * ridimensioniamo nel browser prima di spedirla — una foto di cantiere scende a
 * qualche centinaio di KB, e da cantiere con la rete lenta parte comunque.
 *
 * 2000 px sul lato lungo restano più che sufficienti per documentare una traccia
 * o una crepa: è il doppio di quanto serve per stamparla in A4.
 */

const LATO_MAX = 2000;
const QUALITA = 0.82;

export interface FilePronto {
  data: string; // data URL base64
  mediaType: 'image/jpeg' | 'image/png' | 'image/webp';
  filename: string;
  /** Byte stimati dopo la codifica base64, per avvisare prima di tentare l'upload. */
  size: number;
}

function leggiComeDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Lettura del file non riuscita'));
    reader.readAsDataURL(file);
  });
}

function caricaImmagine(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Immagine non leggibile'));
    img.src = dataUrl;
  });
}

/** Byte reali rappresentati da un data URL base64. */
export function pesoDataUrl(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * Ridimensiona e ricomprime una foto. I file non-immagine tornano intatti:
 * un PDF allegato a un rilievo non va toccato.
 */
export async function preparaFoto(file: File): Promise<FilePronto> {
  const dataUrlOriginale = await leggiComeDataUrl(file);

  const ridimensionabile = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!ridimensionabile) {
    return {
      data: dataUrlOriginale,
      mediaType: file.type as FilePronto['mediaType'],
      filename: file.name,
      size: pesoDataUrl(dataUrlOriginale),
    };
  }

  const img = await caricaImmagine(dataUrlOriginale);
  const lato = Math.max(img.width, img.height);
  const scala = lato > LATO_MAX ? LATO_MAX / lato : 1;

  // Già piccola e leggera: non la ricomprimo, ricomprimere due volte peggiora e basta
  if (scala === 1 && file.size < 900 * 1024) {
    return {
      data: dataUrlOriginale,
      mediaType: file.type as FilePronto['mediaType'],
      filename: file.name,
      size: pesoDataUrl(dataUrlOriginale),
    };
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scala);
  canvas.height = Math.round(img.height * scala);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Niente canvas: meglio provare con l'originale che fallire qui
    return {
      data: dataUrlOriginale,
      mediaType: file.type as FilePronto['mediaType'],
      filename: file.name,
      size: pesoDataUrl(dataUrlOriginale),
    };
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const data = canvas.toDataURL('image/jpeg', QUALITA);
  return {
    data,
    mediaType: 'image/jpeg',
    filename: file.name.replace(/\.(png|webp|jpeg)$/i, '.jpg'),
    size: pesoDataUrl(data),
  };
}

export function formatoPeso(byte: number): string {
  if (byte < 1024) return `${byte} B`;
  if (byte < 1024 * 1024) return `${Math.round(byte / 1024)} KB`;
  return `${(byte / (1024 * 1024)).toFixed(1)} MB`;
}
